import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Image,
  AspectRatio,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Button,
  Skeleton,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { BsBoxSeam, BsSearch, BsTag } from "react-icons/bs";
import { Link, useParams } from "react-router-dom";
import materialsService from "../services/materialsService";
import { useShopping } from "../context/ShoppingContext";

const CategoryMaterials = () => {
  const { categoryId } = useParams();
  const { addToCart } = useShopping();

  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name");

  useEffect(() => {
    setLoading(true);

    materialsService
      .listMaterials({ limit: 1000 })
      .then((result) => {
        const rows = Array.isArray(result) ? result : result?.items || [];

        setMaterials(
          rows.filter(
            (material) =>
              String(material.categoryId?._id || material.categoryId) ===
              String(categoryId),
          ),
        );
      })
      .catch(() => setMaterials([]))
      .finally(() => setLoading(false));
  }, [categoryId]);

  const filteredMaterials = useMemo(() => {
    const query = search.trim().toLowerCase();

    const result = materials.filter((material) => {
      if (!query) return true;

      return (
        material.name?.toLowerCase().includes(query) ||
        material.materialCode?.toLowerCase().includes(query)
      );
    });

    return [...result].sort((a, b) => {
      if (sortBy === "price-low") {
        return Number(a.mrp || 0) - Number(b.mrp || 0);
      }

      if (sortBy === "price-high") {
        return Number(b.mrp || 0) - Number(a.mrp || 0);
      }

      return (a.name || "").localeCompare(b.name || "");
    });
  }, [materials, search, sortBy]);

  return (
    <Box minH="100vh" bg="gray.50">
      <Container maxW="7xl" py={{ base: 6, md: 8 }}>
        {/* Page header */}
        <Flex
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={4}
          mb={6}
        >
          <Box>
            <HStack spacing={2} mb={1}>
              <Box
                w="36px"
                h="36px"
                borderRadius="10px"
                bg="teal.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={BsBoxSeam} color="teal.600" boxSize={4} />
              </Box>

              <Heading size="lg" color="gray.800" letterSpacing="-0.02em">
                Category Materials
              </Heading>
            </HStack>

            <Text color="gray.500" fontSize="sm" ml={{ md: "44px" }}>
              Browse and review materials available in this category.
            </Text>
          </Box>

          {!loading && (
            <Badge
              px={3}
              py={1.5}
              borderRadius="full"
              bg="gray.100"
              color="gray.600"
              textTransform="none"
              fontWeight="600"
            >
              {materials.length}{" "}
              {materials.length === 1 ? "material" : "materials"}
            </Badge>
          )}
        </Flex>

        {/* Toolbar */}
        <Flex
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="xl"
          p={3}
          mb={5}
          gap={3}
          direction={{ base: "column", md: "row" }}
          boxShadow="sm"
        >
          <InputGroup flex="1">
            <InputLeftElement pointerEvents="none">
              <Icon as={BsSearch} color="gray.400" />
            </InputLeftElement>

            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by material name or code..."
              bg="gray.50"
              borderColor="gray.200"
              _hover={{ borderColor: "gray.300" }}
              _focus={{
                bg: "white",
                borderColor: "teal.400",
                boxShadow: "0 0 0 1px var(--chakra-colors-teal-400)",
              }}
            />
          </InputGroup>

          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            width={{ base: "100%", md: "190px" }}
            bg="gray.50"
          >
            <option value="name">Name A–Z</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
          </Select>
        </Flex>

        {/* Loading */}
        {loading && (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Box
                key={item}
                bg="white"
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.200"
                p={5}
              >
                <Skeleton h="18px" w="60%" mb={3} />
                <Skeleton h="14px" w="45%" mb={6} />
                <Skeleton h="35px" w="100%" />
              </Box>
            ))}
          </SimpleGrid>
        )}

        {/* Empty */}
        {!loading && !filteredMaterials.length && (
          <Flex
            minH="300px"
            bg="white"
            border="1px dashed"
            borderColor="gray.300"
            borderRadius="xl"
            align="center"
            justify="center"
            textAlign="center"
          >
            <VStack spacing={3}>
              <Flex
                w="48px"
                h="48px"
                borderRadius="full"
                bg="gray.100"
                align="center"
                justify="center"
              >
                <Icon as={BsBoxSeam} boxSize={5} color="gray.400" />
              </Flex>

              <Box>
                <Text fontWeight="600" color="gray.700">
                  No materials found
                </Text>

                <Text fontSize="sm" color="gray.500">
                  No materials match the selected category or search.
                </Text>
              </Box>
            </VStack>
          </Flex>
        )}

        {/* Materials */}
        {!loading && filteredMaterials.length > 0 && (
          <SimpleGrid
            columns={{
              base: 1,
              md: 2,
              xl: 3,
            }}
            spacing={4}
          >
            {filteredMaterials.map((material) => {
              const quantity = material.inventory?.unrestrictedQuantity ?? 0;

              const uom = material.inventory?.uom || material.uom || "";

              const isActive = !material.status || material.status === "ACTIVE";

              return (
                <Box
                  as={Link}
                  to={`/categories/${categoryId}/${material._id}`}
                  key={material._id}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="xl"
                  overflow="hidden"
                  transition="all 0.18s ease"
                  cursor="pointer"
                  _hover={{
                    borderColor: "teal.300",
                    boxShadow: "md",
                    transform: "translateY(-2px)",
                  }}
                >
                  <AspectRatio ratio={4 / 3}><Image src="https://m.media-amazon.com/images/I/61-O10+QdeL._SL1500_.jpg" alt={material.name} objectFit="cover" /></AspectRatio>
                  <Box p={5}>
                    <Flex justify="space-between" align="flex-start" gap={4}>
                      <Box minW={0}>
                        <Heading
                          size="sm"
                          color="gray.800"
                          noOfLines={1}
                          mb={1}
                        >
                          {material.name || "Unnamed Material"}
                        </Heading>

                        <HStack spacing={1.5}>
                          <Icon as={BsTag} boxSize={3} color="gray.400" />

                          <Text fontSize="sm" color="gray.500" noOfLines={1}>
                            {material.materialCode || "No material code"}
                          </Text>
                        </HStack>
                      </Box>

                      <Badge
                        flexShrink={0}
                        borderRadius="full"
                        px={2.5}
                        py={1}
                        bg={isActive ? "green.50" : "gray.100"}
                        color={isActive ? "green.700" : "gray.600"}
                        textTransform="capitalize"
                        fontSize="xs"
                      >
                        {material.status?.toLowerCase() || "active"}
                      </Badge>
                    </Flex>

                    <Box h="1px" bg="gray.100" my={4} />

                    <SimpleGrid columns={2} spacing={4}>
                      <Stack spacing={0.5}>
                        <Text fontSize="xs" color="gray.500" fontWeight="500">
                          MRP
                        </Text>

                        <Text fontSize="lg" fontWeight="700" color="gray.800">
                          ₹{Number(material.mrp || 0).toLocaleString("en-IN")}
                        </Text>
                      </Stack>

                      <Stack spacing={0.5}>
                        <Text fontSize="xs" color="gray.500" fontWeight="500">
                          Available Stock
                        </Text>

                        <Text
                          fontSize="lg"
                          fontWeight="700"
                          color={
                            Number(quantity) > 0 ? "gray.800" : "orange.500"
                          }
                        >
                          {Number(quantity).toLocaleString("en-IN")}{" "}
                          <Text
                            as="span"
                            fontSize="xs"
                            fontWeight="500"
                            color="gray.500"
                          >
                            {uom}
                          </Text>
                        </Text>
                      </Stack>
                    </SimpleGrid>
                  </Box>

                  <Flex
                    px={5}
                    py={3}
                    bg="gray.50"
                    borderTop="1px solid"
                    borderColor="gray.100"
                    justify="space-between"
                    align="center"
                  >
                    <Text fontSize="xs" color="gray.500">
                      Cost price
                    </Text>

                    <Text fontSize="sm" fontWeight="600" color="gray.700">
                      ₹{Number(material.costPrice || 0).toLocaleString("en-IN")}
                    </Text>
                  </Flex>
                  <Button size="sm" colorScheme="teal" m={4} mt={0} width="calc(100% - 2rem)" onClick={(e) => { e.preventDefault(); addToCart({ ...material, quantity: 1 }); }}>Add to Cart</Button>
                </Box>
              );
            })}
          </SimpleGrid>
        )}
      </Container>
    </Box>
  );
};

export default CategoryMaterials;
